import { TypedDocumentNode, gql } from '@apollo/client';
import { compareDesc } from 'date-fns';

import { Position } from '__generated__/globalTypes';
import { Transfers } from 'components/seo/Team/Transfers';
import { TransferDirection } from 'components/seo/Team/Transfers/Transfer';
import {
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_TEAMS_SLUG,
} from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import { type FragmentOf, unmaskFragment } from 'lib/graphql';
import { generateSportPath } from 'lib/routing/generateSportPath';

import type { Departures_TeamInterface } from './__generated__/index.graphql';

type Props = {
  team: FragmentOf<Departures_TeamInterface>;
};

export const Departures = ({ team: team_ }: Props) => {
  const team = unmaskFragment(team_);
  const { sport } = useSportContext();
  const lastArrivals = team.activeMemberships.nodes
    .filter(membership => membership.player.position !== Position.Coach)
    .sort((a, b) => compareDesc(a.startDate, b.startDate))
    .slice(0, 5);

  const club = {
    ...team,
    displayName: team.name,
    pictureUrl: team.pictureSecondaryUrl,
  };
  const transfers = [
    ...team.recentDepartures.nodes.map(player => ({
      id: player.slug,
      direction: TransferDirection.OUT,
      player: {
        ...player,
        pictureUrl: player.avatarPictureUrl,
        to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
          params: { slug: player.slug },
          sport,
        }),
      },
      date: player.beforeLastOwningMembership?.endDate,
      ...(player.activeClub && {
        club: {
          slug: player.activeClub.slug,
          code: player.activeClub.code,
          displayName: player.activeClub.name,
          pictureUrl: player.activeClub.pictureSecondaryUrl,
          to: generateSportPath(ANY_SPORT_TEAMS_SLUG, {
            params: {
              slug: player.activeClub.slug,
            },
            sport,
          }),
        },
      }),
    })),
    ...lastArrivals.map(membership => ({
      id: membership.id,
      direction: TransferDirection.IN,
      player: {
        ...membership.player,
        pictureUrl: membership.player.avatarPictureUrl,
        to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
          params: {
            slug: membership.player.slug,
          },
          sport,
        }),
      },
      date: membership.startDate,
      ...(membership.player.beforeLastOwningMembership && {
        club: {
          slug: membership.player.beforeLastOwningMembership.team.slug,
          code: membership.player.beforeLastOwningMembership.team.code,
          displayName: membership.player.beforeLastOwningMembership.team.name,
          pictureUrl:
            membership.player.beforeLastOwningMembership.team
              .pictureSecondaryUrl,
          to: generateSportPath(ANY_SPORT_TEAMS_SLUG, {
            params: {
              slug: membership.player.beforeLastOwningMembership.team.slug,
            },
            sport,
          }),
        },
      }),
    })),
  ]
    .sort((a, b) => compareDesc(a.date || 0, b.date || 0))
    .slice(0, 5);

  return <Transfers club={club} transfers={transfers} />;
};

Departures.fragments = {
  TeamInterface: gql`
    fragment Departures_TeamInterface on TeamInterface {
      slug
      name
      code
      pictureSecondaryUrl
      activeMemberships(first: 100) {
        nodes {
          id
          player {
            slug
            position
            displayName
            avatarPictureUrl: pictureUrl(derivative: "avatar")
            country {
              slug
              name
            }
            beforeLastOwningMembership {
              id
              team {
                slug
                name
                code
                pictureSecondaryUrl
              }
            }
          }
          startDate
        }
      }
      recentDepartures(first: 5) {
        nodes {
          slug
          position
          displayName
          avatarPictureUrl: pictureUrl(derivative: "avatar")
          activeClub {
            slug
            name
            code
            pictureSecondaryUrl
          }
          beforeLastOwningMembership {
            id
            endDate
          }
        }
      }
    }
  ` as TypedDocumentNode<Departures_TeamInterface>,
};
